/**
 * JWT Token Utilities
 *
 * Client-side token validation and parsing utilities for authentication system.
 * Provides safe JWT handling with proper error handling and TypeScript types.
 */

/**
 * Decode a JWT's base64url payload as UTF-8.
 * Works in both browser and Node.js environments
 */
const decodeBase64 = (str: string): string => {
  try {
    // JWT uses URL-safe '-'/'_' and omits padding; browser atob expects base64.
    const base64 = str.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    // Browser environment
    if (typeof window !== "undefined" && window.atob) {
      // atob returns bytes, not UTF-8 text. Decode names such as Željko correctly.
      const bytes = window.atob(padded);
      return decodeURIComponent(
        Array.from(bytes, (byte) =>
          `%${byte.charCodeAt(0).toString(16).padStart(2, "0")}`
        ).join("")
      );
    }
    // Node.js environment (for testing, SSR, etc.)
    if (typeof Buffer !== "undefined") {
      return Buffer.from(padded, "base64").toString("utf-8");
    }
    // Fallback - should not happen in normal environments
    throw new Error("No base64 decoder available");
  } catch (error) {
    throw new Error(`Failed to decode base64 string: ${error}`);
  }
};

interface JWTDateObject {
  year: number;
  month: string;
  monthValue: number;
  dayOfMonth: number;
  hour: number;
  minute: number;
  second: number;
  nano: number;
  dayOfWeek: string;
  dayOfYear: number;
  chronology: {
    calendarType: string;
    id: string;
  };
}

export interface JWTPayload {
  exp: number;
  user_name: string;
  scope: string[];
  userAuthorities: string[];
  authorities: string[];
  jti: string;
  client_id: string;
  userDetails: {
    id: number;
    username: string;
    nickname: string | null;
    password: string;
    firstName: string;
    secondName: string;
    phoneNumber: string | null;
    userPhoto: string | null;
    accountExpired: boolean;
    accountLocked: boolean;
    credentialsExpired: boolean;
    enabled: boolean;
    deleted: number;
    accountNonExpired: boolean;
    accountNonLocked: boolean;
    credentialsNonExpired: boolean;
    lbo: any | null;
    created: JWTDateObject;
    modified: JWTDateObject | null;
  };
}

export interface OAuthLoginResponse {
  access_token: string;
  token_type: string;
  refresh_token: string;
  expires_in: number;
  scope: string;
  userAuthorities: string[];
  userDetails: {
    id: number;
    username: string;
    nickname: string | null;
    password: string;
    firstName: string;
    secondName: string;
    phoneNumber: string | null;
    userPhoto: string | null;
    accountExpired: boolean;
    accountLocked: boolean;
    credentialsExpired: boolean;
    enabled: boolean;
    created: string; // ISO string format
    modified: string | null; // ISO string or null
    deleted: number;
    accountNonExpired: boolean;
    accountNonLocked: boolean;
    credentialsNonExpired: boolean;
    lbo: any | null;
  };
  jti: string;
}

export interface MarketplaceInfoResponse {
  marketplaceId: number;
  branchMarketplaceId: number;
  marketplaceUserEmail: string | null;
  name: string;
  takesOrders: number;
}

/**
 * Checks if a JWT token is expired by comparing its expiration time with current time
 * @param token - JWT token string
 * @returns true if token is expired or invalid, false if still valid
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload: JWTPayload = JSON.parse(decodeBase64(token.split(".")[1]));
    const currentTime = Date.now() / 1000; // Convert to seconds

    console.log(
      `Auth: Token expires at ${new Date(
        payload.exp * 1000
      ).toISOString()}, current time: ${new Date().toISOString()}`
    );

    return payload.exp < currentTime;
  } catch (error) {
    console.error("Auth: Invalid token format, treating as expired", error);
    return true; // Treat invalid tokens as expired for security
  }
};

/**
 * Safely parses a JWT token and returns its payload
 * @param token - JWT token string
 * @returns JWT payload object or null if parsing fails
 */
export const getTokenPayload = (token: string): JWTPayload | null => {
  try {
    const payload = JSON.parse(decodeBase64(token.split(".")[1]));
    return payload as JWTPayload;
  } catch (error) {
    console.error("Auth: Failed to parse token payload", error);
    return null;
  }
};

/**
 * Determines if a token should be refreshed based on its expiration time
 * Tokens are refreshed if they expire within the specified threshold
 * @param token - JWT token string
 * @param thresholdMinutes - Minutes before expiration to trigger refresh (default: 5)
 * @returns true if token should be refreshed, false otherwise
 */
export const shouldRefreshToken = (
  token: string,
  thresholdMinutes: number = 5
): boolean => {
  const payload = getTokenPayload(token);
  if (!payload) {
    console.log("Auth: Invalid token, refresh not possible");
    return false;
  }

  const thresholdMs = thresholdMinutes * 60 * 1000;
  const timeUntilExpiry = payload.exp * 1000 - Date.now();

  const shouldRefresh = timeUntilExpiry < thresholdMs && timeUntilExpiry > 0;

  if (shouldRefresh) {
    console.log(
      `Auth: Token expires in ${Math.round(
        timeUntilExpiry / 1000 / 60
      )} minutes, should refresh`
    );
  }

  return shouldRefresh;
};

/**
 * Extracts user authorities/roles from JWT token
 * @param token - JWT token string
 * @returns array of user authorities or empty array if invalid
 */
export const getTokenAuthorities = (token: string): string[] => {
  const payload = getTokenPayload(token);
  return payload?.userAuthorities || payload?.authorities || [];
};

/**
 * Checks if user has a specific role based on JWT token
 * @param token - JWT token string
 * @param role - Role to check for (e.g., 'ADMIN', 'PHARMACIST_SUPPORT_USER')
 * @returns true if user has the role, false otherwise
 */
export const hasRole = (token: string, role: string): boolean => {
  const authorities = getTokenAuthorities(token);
  return authorities.includes(role);
};

/**
 * Gets user ID from JWT token
 * @param token - JWT token string
 * @returns user ID as number or null if not available
 */
export const getTokenUserId = (token: string): number | null => {
  const payload = getTokenPayload(token);
  return payload?.userDetails?.id || null;
};

/**
 * Validates token format without parsing (basic structure check)
 * @param token - JWT token string
 * @returns true if token has proper JWT structure (3 parts separated by dots)
 */
export const isValidTokenFormat = (token: string): boolean => {
  if (!token || typeof token !== "string") {
    return false;
  }

  const parts = token.split(".");
  return parts.length === 3 && parts.every((part) => part.length > 0);
};

/**
 * Comprehensive token validation
 * Checks format, parsing, and expiration in one function
 * @param token - JWT token string
 * @returns object with validation results and details
 */
export const validateToken = (
  token: string
): {
  isValid: boolean;
  isExpired: boolean;
  hasValidFormat: boolean;
  payload: JWTPayload | null;
  error?: string;
} => {
  // Check basic format
  if (!isValidTokenFormat(token)) {
    return {
      isValid: false,
      isExpired: true,
      hasValidFormat: false,
      payload: null,
      error: "Invalid token format",
    };
  }

  // Parse payload
  const payload = getTokenPayload(token);
  if (!payload) {
    return {
      isValid: false,
      isExpired: true,
      hasValidFormat: true,
      payload: null,
      error: "Failed to parse token payload",
    };
  }

  // Check expiration
  const expired = isTokenExpired(token);

  return {
    isValid: !expired,
    isExpired: expired,
    hasValidFormat: true,
    payload,
    error: expired ? "Token is expired" : undefined,
  };
};
